/**
 * Cadence for the post-download upgrade nudge.
 *
 * A free user who just downloaded a clip is at the one unambiguous "I got
 * what I came for" moment in the product — every other upsell surface fires
 * on a block or a failure. The nudge escalates instead of repeating: a heavy
 * first session must not burn every show in one afternoon, which is why the
 * milestones spread out rather than firing every N downloads.
 */

export const DOWNLOAD_UPSELL_MILESTONES = [1, 3, 7, 15] as const;

export const DOWNLOAD_UPSELL_COUNT_KEY = 'autoclipper:download-upsell:count';

const MILESTONES = new Set<number>(DOWNLOAD_UPSELL_MILESTONES);

function isUsableCount(count: number): boolean {
  return Number.isInteger(count) && count > 0;
}

/** Whether the download that brought the running total to `count` earns a nudge. */
export function shouldShowAfterDownload(count: number): boolean {
  if (!isUsableCount(count)) return false;
  return MILESTONES.has(count);
}

export function getDownloadUpsellStorage(browser: object): Storage | null {
  try {
    return (browser as { readonly localStorage?: Storage }).localStorage ?? null;
  } catch {
    return null;
  }
}

/** The stored lifetime download count, or 0 when it is missing or unusable. */
export function readDownloadCount(
  storage: Pick<Storage, 'getItem'> | null,
): number {
  if (!storage) return 0;

  try {
    const stored = storage.getItem(DOWNLOAD_UPSELL_COUNT_KEY);
    if (stored === null || stored === '') return 0;

    const parsed = Number(stored);
    return isUsableCount(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

/**
 * Counts one completed download and returns the new running total.
 *
 * Returns 0 — which `shouldShowAfterDownload` reads as "stay silent" — when
 * the count cannot be persisted. Showing a nudge we failed to record would
 * pin the user at the same milestone and fire on every future download.
 */
export function recordDownload(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null,
): number {
  if (!storage) return 0;

  const next = readDownloadCount(storage) + 1;

  try {
    storage.setItem(DOWNLOAD_UPSELL_COUNT_KEY, String(next));
  } catch {
    return 0;
  }

  return next;
}
